import { useState } from "react";
import { motion } from "framer-motion";
import { Reveal, Eyebrow } from "./Reveal";

/* 10% at TGE + 90% vested linearly over 12 months (per the Genesis proposal) */
const TGE = 10;
const MONTHS = 12;
const PER_MONTH = (100 - TGE) / MONTHS;
const POINTS = [{ m: 0, pct: TGE }].concat(
    Array.from({ length: MONTHS }, (_, i) => ({
        m: i + 1,
        pct: Math.min(100, TGE + PER_MONTH * (i + 1)),
    }))
);

const W = 560;
const H = 240;
const PX = 42;
const PY = 30;
const x = (m) => PX + (m / MONTHS) * (W - 2 * PX);
const y = (p) => H - PY - (p / 100) * (H - 2 * PY);

const line = POINTS.map((p, i) => `${i ? "L" : "M"}${x(p.m).toFixed(1)},${y(p.pct).toFixed(1)}`).join(" ");
const area = `${line} L${x(MONTHS).toFixed(1)},${y(0)} L${x(0)},${y(0)} Z`;
const fmtP = (p) => (Number.isInteger(p) ? `${p}%` : `${p.toFixed(1)}%`);

const STATS = [
    { t: "10%", l: "Unlocked at TGE", tid: "vesting-stat-tge" },
    { t: "90%", l: "Vested over 12 months", tid: "vesting-stat-vest" },
    { t: "7.5%", l: "Per month, linear", tid: "vesting-stat-rate" },
];

export default function VestingTimeline() {
    const [active, setActive] = useState(null);

    return (
        <section id="vesting" data-testid="vesting-section" className="relative overflow-hidden bg-base py-24 lg:py-32">
            <div className="relative mx-auto max-w-7xl px-5 sm:px-8 lg:px-12">
                <div className="mx-auto max-w-3xl text-center">
                    <Reveal>
                        <Eyebrow className="justify-center">Vesting Schedule</Eyebrow>
                    </Reveal>
                    <Reveal delay={0.08}>
                        <h2 className="font-grotesk text-2xl font-bold uppercase leading-tight tracking-tight text-white sm:text-3xl lg:text-4xl">
                            A measured <span className="text-grad-blue">unlock.</span>
                        </h2>
                    </Reveal>
                    <Reveal delay={0.14}>
                        <p className="mt-5 text-base leading-relaxed text-slate-400">
                            Genesis allocations follow a 10% release at TGE, with the
                            remaining 90% vesting linearly over 12 months.
                        </p>
                    </Reveal>
                </div>

                <Reveal delay={0.2}>
                    <div className="mx-auto mt-10 flex max-w-3xl flex-wrap items-center justify-center gap-3">
                        {STATS.map((s) => (
                            <div
                                key={s.t}
                                data-testid={s.tid}
                                className="flex items-baseline gap-2.5 rounded-full border border-white/10 bg-white/[0.03] px-5 py-2.5"
                            >
                                <span className="font-mono text-sm font-semibold text-cyber">{s.t}</span>
                                <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-slate-400">
                                    {s.l}
                                </span>
                            </div>
                        ))}
                    </div>
                </Reveal>

                <Reveal delay={0.25}>
                    <div
                        data-testid="vesting-chart"
                        className="relative mx-auto mt-10 max-w-4xl overflow-hidden rounded-xl border border-white/[0.07] bg-panel/40 p-5 sm:p-8"
                    >
                        <div className="relative" style={{ aspectRatio: `${W} / ${H}` }}>
                            <svg
                                viewBox={`0 0 ${W} ${H}`}
                                className="h-full w-full"
                                onMouseLeave={() => setActive(null)}
                            >
                                <defs>
                                    <linearGradient id="vest-line" x1="0" y1="0" x2="1" y2="0">
                                        <stop offset="0%" stopColor="#00D1FF" />
                                        <stop offset="100%" stopColor="#2563EB" />
                                    </linearGradient>
                                    <linearGradient id="vest-area" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor="#00D1FF" stopOpacity="0.22" />
                                        <stop offset="100%" stopColor="#00D1FF" stopOpacity="0" />
                                    </linearGradient>
                                </defs>

                                {[0, 25, 50, 75, 100].map((g) => (
                                    <g key={g}>
                                        <line x1={PX} x2={W - PX} y1={y(g)} y2={y(g)} stroke="rgba(255,255,255,0.05)" strokeWidth="0.75" />
                                        <text
                                            x={PX - 8}
                                            y={y(g) + 2.5}
                                            textAnchor="end"
                                            fontSize="8"
                                            fill="rgba(148,163,184,0.85)"
                                            style={{ fontFamily: "'JetBrains Mono', monospace" }}
                                            dangerouslySetInnerHTML={{ __html: `${g}%` }}
                                        />
                                    </g>
                                ))}

                                <motion.path
                                    d={area}
                                    fill="url(#vest-area)"
                                    initial={{ opacity: 0 }}
                                    whileInView={{ opacity: 1 }}
                                    viewport={{ once: true, amount: 0.35 }}
                                    transition={{ duration: 1.4, delay: 0.9 }}
                                />
                                <motion.path
                                    d={line}
                                    fill="none"
                                    stroke="url(#vest-line)"
                                    strokeWidth="2"
                                    strokeLinejoin="round"
                                    initial={{ pathLength: 0 }}
                                    whileInView={{ pathLength: 1 }}
                                    viewport={{ once: true, amount: 0.35 }}
                                    transition={{ duration: 1.8, ease: [0.22, 1, 0.36, 1] }}
                                />

                                {POINTS.map((p, i) => (
                                    <g key={p.m}>
                                        <text
                                            x={x(p.m)}
                                            y={H - 10}
                                            textAnchor="middle"
                                            fontSize="8"
                                            fill="rgba(148,163,184,0.85)"
                                            style={{ fontFamily: "'JetBrains Mono', monospace" }}
                                            dangerouslySetInnerHTML={{ __html: p.m === 0 ? "TGE" : `M${p.m}` }}
                                        />
                                        <motion.circle
                                            cx={x(p.m)}
                                            cy={y(p.pct)}
                                            r={active === i ? 4 : 2.4}
                                            fill={active === i ? "#00D1FF" : i === 0 ? "#00D1FF" : "#38BDF8"}
                                            stroke={active === i ? "rgba(0,209,255,0.4)" : "none"}
                                            strokeWidth="6"
                                            initial={{ opacity: 0 }}
                                            whileInView={{ opacity: 1 }}
                                            viewport={{ once: true, amount: 0.35 }}
                                            transition={{ duration: 0.4, delay: 0.3 + i * 0.08 }}
                                            style={{ cursor: "pointer", filter: active === i ? "drop-shadow(0 0 6px rgba(0,209,255,0.9))" : "none" }}
                                            onMouseEnter={() => setActive(i)}
                                        />
                                        <circle cx={x(p.m)} cy={y(p.pct)} r="14" fill="transparent" onMouseEnter={() => setActive(i)} />
                                    </g>
                                ))}
                            </svg>

                            {active !== null && (
                                <div
                                    className="pointer-events-none absolute z-10 -translate-x-1/2 -translate-y-full"
                                    style={{
                                        left: `${(x(POINTS[active].m) / W) * 100}%`,
                                        top: `${(y(POINTS[active].pct) / H) * 100 - 2}%`,
                                    }}
                                >
                                    <div className="rounded-md border border-cyber/30 bg-void/90 px-3 py-1.5 text-center backdrop-blur">
                                        <div className="font-mono text-xs font-semibold text-cyber">
                                            {fmtP(POINTS[active].pct)} unlocked
                                        </div>
                                        <div className="font-mono text-[8px] uppercase tracking-[0.2em] text-slate-500">
                                            {POINTS[active].m === 0 ? "At TGE" : `Month ${POINTS[active].m}`}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        <p className="mt-4 text-center font-mono text-[10px] uppercase tracking-[0.18em] text-slate-600">
                            Cumulative unlock — Genesis vesting per the TNX Genesis Tokenomics Proposal
                        </p>
                    </div>
                </Reveal>
            </div>
        </section>
    );
}
